import React, {useEffect, useState, useMemo} from 'react'
import 'bootstrap/dist/css/bootstrap.min.css'
import {LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend} from 'recharts'
import axios from 'axios'
import moment from 'moment'
import {useReduxAuth} from '../../../auth'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {getTodayDateTime} from '../../helper/helperFunction'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'
import {USER_ROLES} from '../../../../constants/StatusConstants'

// API response shape
interface APIDataPoint {
  userId: number
  name: string
  day: string // YYYY-MM-DD
  minutes: number
}

// Chart data shape: day plus one key per user name
interface ChartDataPoint {
  day: string // YYYY-MM-DD
  [userName: string]: string | number
}

// Color palette for lines
const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#d0ed57', '#a4de6c', '#ff7300', '#413ea0']

const StaffDoctorMinutesChart: React.FC = () => {
  const {currentUser} = useReduxAuth()
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  
  const [apiData, setApiData] = useState<APIDataPoint[]>([])

  // Fetch raw API data and sanitize names
  useEffect(() => {
    const fetchData = async () => {
      // Skip if MA and no doctor selected
      if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && !maSelectedDoctorId) {
        setApiData([])
        return
      }

      // For MA, use selected doctor ID; for others, use currentUser.id
      const doctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
        ? maSelectedDoctorId
        : currentUser?.id

      if (!doctorId) return

      try {
        // Build query params for MA
        const queryParams = new URLSearchParams()
        if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId) {
          queryParams.append('doctor_id', maSelectedDoctorId.toString())
        }
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago'
        queryParams.append('timezone', timezone)

        const response = await axios.get(
          API_ENDPOINTS.GET_DOCTOR_TEAM_DAILY_SUMMARY(doctorId, getTodayDateTime, queryParams.toString())
        )
        const raw: APIDataPoint[] = response.data.data ?? []

        // Strip any trailing "null" from name
        const data: APIDataPoint[] = raw.map((d) => ({
          ...d,
          name: d.name.replace(/\s*null\s*$/i, '').trim(),
        }))

        setApiData(data)
      } catch (error) {
        console.error(error)
      }
    }
    if (currentUser?.id || (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId)) {
      fetchData()
    }
  }, [currentUser, maSelectedDoctorId])

  // Derive list of distinct user names
  const userNames = useMemo(() => Array.from(new Set(apiData.map((d) => d.name))), [apiData])

  // Generate last 30 days array
  const last30Days = useMemo(() => {
    const days: string[] = []
    for (let i = 29; i >= 0; i--) {
      days.push(moment().subtract(i, 'days').format('YYYY-MM-DD'))
    }
    return days
  }, [])

  // Build chart data: one entry per day, with minutes per user
  const chartData: ChartDataPoint[] = useMemo(() => {
    return last30Days.map((day) => {
      const point: ChartDataPoint = {day}
      userNames.forEach((name) => {
        const entry = apiData.find((d) => d.day === day && d.name === name)
        point[name] = entry ? entry.minutes : 0
      })
      return point
    })
  }, [last30Days, userNames, apiData])

  // Calculate yMax based on all values to show y axis even with no data
  const allValues: number[] = chartData.flatMap((entry) =>
    userNames.map((name) => Number(entry[name] ?? 0))
  );
  const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0; 
  const yMax = maxValue + 5 || 60; 
 
  return (
    <div
      className='p-4 bg-white rounded-4 shadow-sm'
      style={{border: '1px solid #E0E0E0', width: '100%'}}
    >
      <div className='d-flex align-items-center gap-2'>
        <h3>Staff Care Minutes</h3>
        <span className='text-muted'>(Last 30 Days)</span>
      </div>
      <ResponsiveContainer width='100%' height={363}>
        <LineChart data={chartData} margin={{top: 20, right: 20, left: 0, bottom: 0}}>
          <XAxis
            dataKey='day'
            tickFormatter={(val: string) => moment(val, 'YYYY-MM-DD').format('MMM DD')}
            angle={-45}
            textAnchor='end'
            height={50}
          />
          <YAxis
            label={{
              title: 'Time Spent (minutes)',
              value: 'Time Spent (minutes)',
              angle: -90,
              position: 'left',
              dx: 20,
              dy: -70,
            }}
            domain={[0, yMax]}
            allowDataOverflow
            tickFormatter={(value) =>
              Number.isInteger(value) ? value.toString() : value.toFixed(2)
            }
          />
          <Tooltip labelFormatter={(label) => moment(label, 'YYYY-MM-DD').format('MMMM D, YYYY')} />
          <Legend align='center' verticalAlign='top' />
          {userNames.map((name, idx) => (
            <Line
              key={name}
              stroke={COLORS[idx % COLORS.length]}
              type='monotone'
              dataKey={name}
              name={name}
              strokeWidth={3}
              dot={{r: 3}}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

export default StaffDoctorMinutesChart
